import { useEffect, useState } from 'react';
import StepFormTemplate from "../../templates/step-form.template";
import PersonalInfoForm from "../../templates/personal-info-form.template";
import { CURP } from '@randyd45/curp-validation';
import { esEmailValido } from '../../../utils/email';
import { useNavigate } from 'react-router-dom';
import { validateCurpService } from "../../../services/new-services/public-v2.services.js";
import CurpConfirmationTemplate from "../../templates/curp-duplicate-confirmation.template";
import { useDispatch } from "react-redux";
import { updatePersonalInformation } from "../../../redux/slices/application-process.slice.js";
import { createWebLogRecord } from "../../../services/new-services/analytics.service.js";
import { useSelector } from 'react-redux';

const ApplicationFirstStepScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        curp: '',
        rfc: '',
        homoclave: '',
        firstName: '',
        lastName: '',
        motherLastName: '',
        email: '',
    });
    const sessionId = useSelector(state => state.applicationProcess.sessionId);
    const [isLoading, setIsLoading] = useState(false);
    const [informationRetrieved, setInformationRetrieved] = useState(false);
    const [curpObject, setCurpObject] = useState(null);
    const [isInformationValid, setIsInformationValid] = useState(false);
    const [showCurpConfirmation, setShowCurpConfirmation] = useState(false);

    const [errors, setErrors] = useState({
        curp: '',
        firstName: '',
        lastName: '',
        motherLastName: '',
        email: '',
        homoclave: '',
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            rfc: formData.curp.substring(0, 10)
        }));

        if (formData.curp.length == 18) {
            setIsLoading(true);
            const curpObject = new CURP(formData.curp);
            setCurpObject(curpObject);
            const birthDay = curpObject.extractBirthday();
            const currentDate = new Date();
            const isAdult = currentDate.getFullYear() - birthDay.year >= 18 ? true : currentDate.getMonth() > birthDay.month - 1 ? true : currentDate.getDate() >= birthDay.day;
            if (!isAdult) {
                setErrors(prev => ({
                    ...prev,
                    curp: 'Debes ser mayor de edad para poder aplicar'
                }));
                setIsLoading(false);
                return;
            }
            if (!curpObject.isFormatValid()) {
                createWebLogRecord(sessionId, null, {
                    curp: formData.curp,
                    formatValid: curpObject.isFormatValid(),
                    adult: !curpObject.isFormatValid(),
                }, true, false, 'USER_INPUT_CURP');
                setErrors(prev => ({
                    ...prev,
                    curp: 'El CURP no es válido'
                }));
                setIsLoading(false);
                return;
            }
            validateCurpService(formData.curp).then(res => {
                console.log(res.success && res.data?.dataRetrieved);
                if (res.success && res.data?.dataRetrieved && !res.data?.duplicated) {
                    createWebLogRecord(sessionId, null, {
                        data: { ...res.data },
                        curp: formData.curp,
                    }, false, true, 'USER_INPUT_CURP');
                    setInformationRetrieved(true);
                    setFormData(state => ({
                        ...state,
                        firstName: res.data.nameData.firstName,
                        lastName: res.data.nameData.lastName_1,
                        motherLastName: res.data.nameData.lastName_2
                    }));
                    setErrors(prev => ({
                        ...prev,
                        curp: ''
                    }));
                } else if (res.success && res.data?.duplicated) {
                    createWebLogRecord(sessionId, null, {
                        curp: formData.curp,
                        duplicated: res.data?.duplicated,
                        dataRetrieved: res.data?.dataRetrieved,
                    }, true, false, 'USER_INPUT_CURP');
                    setShowCurpConfirmation(true);
                    setErrors(prev => ({
                        ...prev,
                        curp: 'El CURP ya está en uso'
                    }));
                } else if (res.success) {
                    createWebLogRecord(sessionId, null, {
                        curp: formData.curp,
                    }, false, true, 'USER_INPUT_CURP');
                    setInformationRetrieved(false);
                }
                setIsLoading(false);
            })
        } else {
            setErrors(prev => ({
                ...prev,
                curp: ''
            }));
        }
    }, [formData.curp]);

    const handleChange = (field) => (value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        if (formData.firstName.length >= 3) {
            setErrors(prev => ({
                ...prev,
                firstName: curpObject.isNameValid(formData.firstName) ? '' : 'El nombre no coincide con el CURP'
            }));
        }
        if (formData.lastName.length >= 3) {
            setErrors(prev => ({
                ...prev,
                lastName: curpObject.isFirstLastnameValid(formData.lastName) ? '' : 'El apellido no coincide con el CURP'
            }));
        }
        if (formData.motherLastName.length >= 3) {
            setErrors(prev => ({
                ...prev,
                motherLastName: curpObject.isSecondLastnameValid(formData.motherLastName) ? '' : 'El apellido materno no coincide con el CURP'
            }));
        }
        if (formData.homoclave.length > 0) {
            setErrors(prev => ({
              ...prev,
              homoclave: formData.homoclave.length !== 3 ? 'La homoclave debe tener 3 caracteres' : ''
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              homoclave: '',
            }));
        }
        setIsInformationValid(curpObject?.isFormatValid() && 
        curpObject?.isNameValid(formData.firstName) && 
        curpObject?.isFirstLastnameValid(formData.lastName) && 
        curpObject?.isSecondLastnameValid(formData.motherLastName) && 
        esEmailValido(formData.email) && 
        (formData.homoclave.length === 3 || formData.homoclave.length === 0));

    }, [formData.firstName, formData.lastName, formData.motherLastName, formData.email, formData.homoclave]);

    function handleSubmit() {
        dispatch(updatePersonalInformation({
            curp: formData.curp,
            homoclave: formData.homoclave,
            firstName: formData.firstName,
            lastName_1: formData.lastName,
            lastName_2: formData.motherLastName,
            primaryEmail: formData.email,
        }))
        createWebLogRecord(sessionId, null, {
            email: formData.email,
            isEmailValid: esEmailValido(formData.email),
        }, false, true, 'USER_INPUT_EMAIL');
        createWebLogRecord(sessionId, null, {
            curp: formData.curp,
            homoclave: formData.homoclave,
            firstName: formData.firstName,
            lastName_1: formData.lastName,
            lastName_2: formData.motherLastName,
            primaryEmail: formData.email,
        }, false, true, 'USER_FINISH_PERSONAL_INFO');
        navigate('/app/application/second-step');
    }

    function handleCurpConfirmation() {
        createWebLogRecord(sessionId, null, {
            curp: formData.curp,
        }, false, true, 'USER_CONFIRM_CURP');
        navigate(`/app/application/repeated-account/${formData.curp}`);
    }

    if (showCurpConfirmation) {
        return (
            <div style={{ ...styles.mainContainerStyle, minHeight: '100vh' }}>
                <CurpConfirmationTemplate curp={formData.curp} onConfirm={handleCurpConfirmation} onEdit={() => setShowCurpConfirmation(false)} />
            </div>
        )
    }

    return (
        <div style={styles.mainContainerStyle}>
            <StepFormTemplate
                title="Datos personales"
                description="Completa la siguiente información"
                currentStep={2}
                totalSteps={5}
                submitEnabled={isInformationValid}
                onSubmit={handleSubmit}
                onClose={() => {
                    navigate('/')
                }}
            >
                <PersonalInfoForm
                    isLoading={isLoading}
                    curp={formData.curp}
                    rfc={formData.rfc}
                    homoclave={formData.homoclave}
                    firstName={formData.firstName}
                    lastName={formData.lastName}
                    motherLastName={formData.motherLastName}
                    email={formData.email}
                    onCurpChange={handleChange('curp')}
                    onHomoclaveChange={handleChange('homoclave')}
                    onFirstNameChange={handleChange('firstName')}
                    onLastNameChange={handleChange('lastName')}
                    onMotherLastNameChange={handleChange('motherLastName')}
                    onEmailChange={handleChange('email')}
                    errors={errors}
                    informationRetrieved={informationRetrieved}
                />
            </StepFormTemplate>
        </div>
    )

};

const styles = {
    mainContainerStyle: {
        margin: '0 auto',
        padding: window.innerWidth > 700 ? '0 25%' : '0'
    }
}

export default ApplicationFirstStepScreen;